import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Business.css';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import PriceManagement from './admin/PriceManagement/PriceManagement';
import Treasury from './admin/Treasury/Treasury';
import ContactMessages from './admin/ContactMessages/ContactMessages';
import { useFirestoreSync } from '../hooks/useFirestoreSync';
import FirebaseTest from '../components/FirebaseTest';
import FirebaseStatus from '../components/FirebaseStatus';

const Business = () => {
  const navigate = useNavigate();
  const packetInputRefs = useRef([]);
  const [darkMode, setDarkMode] = useState(false);
  const [activePage, setActivePage] = useState('dashboard'); // dashboard, farmers, products, revenue, billentry
  const [showRevenueModal, setShowRevenueModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [productTab, setProductTab] = useState('view');
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: '',
    englishName: '',
    currentPrice: 0
  });
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [productQuantities, setProductQuantities] = useState({});
  const [newTransaction, setNewTransaction] = useState({
    type: 'credit',
    amount: '',
    reason: '',
    date: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
  });
  const [billData, setBillData] = useState({
    customerName: '',
    phone: '',
    aadhar: '',
    village: '',
    price: '',
    itemType: '',
    packets: '',
    packetWeights: [],
    bonus: '',
    extraExpensive: '',
    extraWaste: ''
  });

  const [billDetails, setBillDetails] = useState(null);
  const [showBillOutput, setShowBillOutput] = useState(false);
  const [billGenerated, setBillGenerated] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [totalBalance, setTotalBalance] = useState(0);
  const [showBillForm, setShowBillForm] = useState(false);
  const [billStep, setBillStep] = useState(1); // Step 1: Farmer Details, Step 2: Item Details, Step 3: Packet Entry
  const [selectedCustomer, setSelectedCustomer] = useState(null); // For customer detail view
  const [showAllFarmersData, setShowAllFarmersData] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentReason, setPaymentReason] = useState('');
  const [paymentType, setPaymentType] = useState('treasury');
  const [farmerSearchQuery, setFarmerSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showEditFarmerModal, setShowEditFarmerModal] = useState(false);
  const [editingFarmer, setEditingFarmer] = useState(null);

  // Handle body scroll lock when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isMobileMenuOpen]);

  // Analytics state for dashboard metrics
  const [analytics, setAnalytics] = useState({
    totalOrders: 156,
    activeCustomers: 89,
    products: products.length,
    revenue: 0, // Will be updated dynamically
    monthlyGrowth: {
      orders: 12,
      customers: 8,
      revenue: 15
    }
  });

  const expensivePriceDeductionPerPacket = 5; // rupees

  // Initialize Firestore sync
  const { saveBillToFirestore, saveTreasuryToFirestore } = useFirestoreSync(
    customers,
    products,
    setCustomers,
    setProducts
  );

  useEffect(() => {
    // Check if user is logged in
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (!isLoggedIn) {
      navigate('/login');
    }

    // Check for saved dark mode preference
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);

    // Clear old localStorage products cache to force Firebase load
    // This ensures all devices load fresh data from Firebase
    const hasCleared = sessionStorage.getItem('productsCleared');
    if (!hasCleared) {
      localStorage.removeItem('adminProducts');
      sessionStorage.setItem('productsCleared', 'true');
      console.log('✅ Cleared old products cache, loading from Firebase...');
    }

    // Products are now loaded from Firebase via useFirestoreSync hook
    // No localStorage usage for products

    // Update current time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [navigate]);

  // Automatically show bill form when billentry page is active, close when leaving
  useEffect(() => {
    if (activePage === 'billentry') {
      setShowBillForm(true);
    } else {
      setShowBillForm(false);
      setBillStep(1); // Reset to step 1 when leaving
    }
  }, [activePage]);

  // Update analytics when products change
  useEffect(() => {
    setAnalytics(prev => ({
      ...prev,
      products: products.length
    }));
    
    // Update product quantities
    const quantities = {};
    products.forEach(product => {
      quantities[product.key] = product.quantity || 0;
    });
    setProductQuantities(quantities);

    // Set default itemType to first product if not set
    if (products.length > 0 && !billData.itemType) {
      setBillData(prev => ({
        ...prev,
        itemType: products[0].key
      }));
    }
  }, [products, billData.itemType]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    let processedValue = value;
    let cleanedValue = value.replace(/\D/g, ''); // Clean value for validation for all relevant inputs

    // Specific processing and validation based on input id
    if (id === 'phone') {
      // Restrict to maximum 10 digits
      if (cleanedValue.length > 10) {
        cleanedValue = cleanedValue.slice(0, 10);
      }
      processedValue = cleanedValue; // Keep only digits for phone display
      // Basic validation for phone length
      if (cleanedValue.length !== 10) {
        setErrors(prev => ({ ...prev, phone: 'Phone number must be 10 digits.' }));
      } else {
        setErrors(prev => ({ ...prev, phone: null }));
      }
    } else if (id === 'aadhar') {
      // Format Aadhar number with spaces every 4 digits for display
      processedValue = cleanedValue.match(/.{1,4}/g)?.join(' ') || '';

      // Basic validation for aadhar length (on cleaned value)
      if (cleanedValue.length !== 12) {
        setErrors(prev => ({ ...prev, aadhar: 'Aadhar number must be 12 digits.' }));
      } else {
        setErrors(prev => ({ ...prev, aadhar: null }));
      }
    } else if (id === 'packets') {
      // Ensure packets is a number for display and calculation
      processedValue = cleanedValue;
      const numPackets = parseInt(cleanedValue) || 0; // Use cleanedValue for parsing
      setBillData(prev => ({
        ...prev,
        packets: processedValue, // Store cleaned value for packets display
        packetWeights: Array(numPackets).fill('')
      }));
      // Skip the general setBillData at the end for packets to avoid overwriting packetWeights
      return;
    } else if (id === 'bonus' || id === 'extraExpensive') {
      // For bonus and extra expensive, just take the raw value for now
      processedValue = value;
      // Optional: add numeric validation here if needed
    }

    setBillData(prev => ({
      ...prev,
      [id]: processedValue
    }));

  };

  const handlePacketWeightChange = (index, value) => {
    const newPacketWeights = [...billData.packetWeights];
    newPacketWeights[index] = value;
    setBillData(prev => ({
      ...prev,
      packetWeights: newPacketWeights
    }));
  };

  const updateProductQuantities = (itemType, weight) => {
    setProductQuantities(prev => ({
      ...prev,
      [itemType]: prev[itemType] + parseFloat(weight)
    }));

    // Also update products array
    setProducts(prev => prev.map(product =>
      product.key === itemType
        ? { ...product, quantity: product.quantity + parseFloat(weight) }
        : product
    ));
  };

  // Helper function to get product display name (Kannada + English)
  const getProductDisplayName = (itemKey) => {
    const product = products.find(p => p.key === itemKey);
    if (product) {
      return `${product.name} (${product.englishName})`;
    }
    return itemKey ? itemKey.toUpperCase() : 'Unknown Product';
  };

  // Product CRUD functions
  const handleAddProduct = (e) => {
    e.preventDefault();
    if (!newProduct.name || !newProduct.englishName) {
      alert('Please fill in all required fields');
      return;
    }

    const productKey = newProduct.englishName.toLowerCase().replace(/\s+/g, '');
    const initialPrice = parseFloat(newProduct.currentPrice) || 0;
    const product = {
      id: Date.now(),
      name: newProduct.name,
      englishName: newProduct.englishName,
      key: productKey,
      quantity: 0,
      unit: 'kg',
      currentPrice: initialPrice,
      priceHistory: initialPrice > 0 ? [{
        price: initialPrice,
        date: currentTime,
        note: 'Initial price'
      }] : []
    };

    // Update products state - will auto-sync to Firebase via useFirestoreSync
    const updatedProducts = [...products, product];
    setProducts(updatedProducts);

    setProductQuantities(prev => ({
      ...prev,
      [productKey]: 0
    }));

    // Update analytics
    setAnalytics(prev => ({
      ...prev,
      products: prev.products + 1
    }));

    // Reset form and switch to view tab
    setNewProduct({
      name: '',
      englishName: '',
      currentPrice: 0
    });
    setProductTab('view');
    alert('Product added successfully!');
  };

  const handleEditProduct = (product) => {
    setEditingProduct({ ...product });
    setProductTab('edit');
  };

  const handleUpdateProduct = (e) => {
    e.preventDefault();
    if (!editingProduct.name || !editingProduct.englishName) {
      alert('Please fill in all required fields');
      return;
    }

    setProducts(prev => prev.map(p =>
      p.id === editingProduct.id ? editingProduct : p
    ));

    setProductQuantities(prev => ({
      ...prev,
      [editingProduct.key]: editingProduct.quantity
    }));

    setEditingProduct(null);
    setProductTab('view');
    alert('Product updated successfully!');
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    const product = products.find(p => p.id === productId);

    // Delete from Firestore
    try {
      const { deleteProduct } = await import('../services/firestoreService');
      await deleteProduct(productId);
      console.log('Product deleted from Firestore');
    } catch (error) {
      console.error('Error deleting product from Firestore:', error);
    }

    setProducts(prev => prev.filter(p => p.id !== productId));

    // Remove from productQuantities
    const newQuantities = { ...productQuantities };
    delete newQuantities[product.key];
    setProductQuantities(newQuantities);

    // Update analytics
    setAnalytics(prev => ({
      ...prev,
      products: prev.products - 1
    }));

    alert('Product deleted successfully!');
  };

  const handleAddPayment = () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    if (!paymentReason || paymentReason.trim() === '') {
      alert('Please enter a reason for the payment');
      return;
    }

    const amount = parseFloat(paymentAmount);
    const pendingAmount = selectedCustomer.pendingAmount || selectedCustomer.totalAmount;

    if (amount > pendingAmount) {
      alert(`Payment amount cannot exceed pending amount of ₹${pendingAmount.toFixed(2)}`);
      return;
    }

    const payment = {
      amount: amount,
      date: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
      reason: paymentReason.trim()
    };

    const updatedCustomers = customers.map(customer => {
      if (customer.phone === selectedCustomer.phone) {
        const newPaidAmount = (customer.paidAmount || 0) + amount;
        const newPendingAmount = customer.totalAmount - newPaidAmount;
        return {
          ...customer,
          paidAmount: newPaidAmount,
          pendingAmount: newPendingAmount,
          payments: [...(customer.payments || []), payment]
        };
      }
      return customer;
    });

    setCustomers(updatedCustomers);

    // Update selectedCustomer to reflect changes
    const updatedSelectedCustomer = updatedCustomers.find(c => c.phone === selectedCustomer.phone);
    setSelectedCustomer(updatedSelectedCustomer);

    // Add debit entry to treasury only if payment type is 'treasury'
    if (paymentType === 'treasury') {
      const treasuryTransaction = {
        id: Date.now(),
        type: 'debit',
        amount: amount,
        reason: `Payment to ${selectedCustomer.name} - ${paymentReason.trim()}`,
        date: new Date().toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata',
          dateStyle: 'medium',
          timeStyle: 'short'
        }),
        timestamp: Date.now()
      };

      // Get existing treasury transactions
      const savedTransactions = localStorage.getItem('treasuryTransactions');
      const existingTransactions = savedTransactions ? JSON.parse(savedTransactions) : [];

      // Add new transaction
      const updatedTransactions = [...existingTransactions, treasuryTransaction];
      localStorage.setItem('treasuryTransactions', JSON.stringify(updatedTransactions));

      // Save to Firestore
      saveTreasuryToFirestore(treasuryTransaction);
    }

    setPaymentAmount('');
    setPaymentReason('');
    setPaymentType('treasury');
    setShowPaymentModal(false);

    const successMessage = paymentType === 'treasury'
      ? 'Payment recorded successfully and deducted from treasury!'
      : 'Payment recorded successfully (Online payment - not deducted from treasury)!';
    alert(successMessage);
  };

  const getFilteredFarmers = () => {
    let filtered = customers;

    // Apply search filter
    if (farmerSearchQuery.trim() !== '') {
      const query = farmerSearchQuery.toLowerCase();
      filtered = filtered.filter(customer =>
        customer.name.toLowerCase().includes(query) ||
        customer.phone.includes(query) ||
        customer.village.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const handleEditFarmer = (farmer) => {
    setEditingFarmer({
      originalPhone: farmer.phone,
      name: farmer.name,
      phone: farmer.phone,
      aadhar: farmer.aadhar,
      village: farmer.village
    });
    setShowEditFarmerModal(true);
  };

  const handleUpdateFarmer = () => {
    if (!editingFarmer.name || !editingFarmer.phone || !editingFarmer.village) {
      alert('Please fill in all required fields');
      return;
    }

    const updatedCustomers = customers.map(customer => {
      if (customer.phone === editingFarmer.originalPhone) {
        return {
          ...customer,
          name: editingFarmer.name,
          phone: editingFarmer.phone,
          aadhar: editingFarmer.aadhar,
          village: editingFarmer.village
        };
      }
      return customer;
    });

    setCustomers(updatedCustomers);
    setShowEditFarmerModal(false);
    setEditingFarmer(null);
    alert('Farmer updated successfully!');
  };

  const handleDeleteFarmer = async (farmer) => {
    const confirmMessage = `Are you sure you want to delete ${farmer.name}?\n\nThis will permanently delete:\n- ${farmer.totalBills} bill(s)\n- Total amount: ₹${farmer.totalAmount.toFixed(2)}\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Delete from Firestore
      const { deleteCustomer } = await import('../services/firestoreService');
      const result = await deleteCustomer(farmer.phone);
      
      if (result.success) {
        // Remove from local state
        const updatedCustomers = customers.filter(c => c.phone !== farmer.phone);
        setCustomers(updatedCustomers);

        // Close modal if this farmer was selected
        if (selectedCustomer && selectedCustomer.phone === farmer.phone) {
          setSelectedCustomer(null);
        }

        alert('Farmer deleted successfully!');
      } else {
        throw new Error('Failed to delete from Firestore');
      }
    } catch (error) {
      console.error('Error deleting farmer:', error);
      alert('Error deleting farmer. Please try again.');
    }
  };

  const handlePrintFarmersData = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Farmers Data - Kotyal Traders</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              color: #333;
            }
            h1 {
              color: #10b981;
              text-align: center;
              margin-bottom: 10px;
            }
            .header-info {
              text-align: center;
              margin-bottom: 30px;
              color: #666;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 12px;
              text-align: left;
            }
            th {
              background-color: #10b981;
              color: white;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              color: #666;
              font-size: 12px;
            }
            @media print {
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>Kotyal Traders</h1>
          <div class="header-info">
            <p>Farmers Data Report</p>
            <p>Generated on: ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Village</th>
                <th>Total Bills</th>
                <th>Total Amount (₹)</th>
                <th>Paid Amount (₹)</th>
                <th>Pending Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              ${customers.map((customer, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${customer.name}</td>
                  <td>${customer.phone}</td>
                  <td>${customer.village}</td>
                  <td>${customer.totalBills}</td>
                  <td>₹${customer.totalAmount.toFixed(2)}</td>
                  <td style="color: #3b82f6; font-weight: bold;">₹${(customer.paidAmount || 0).toFixed(2)}</td>
                  <td style="color: #ef4444; font-weight: bold;">₹${(customer.pendingAmount || customer.totalAmount).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">
            <p><strong>Total Farmers:</strong> ${customers.length}</p>
            <p><strong>Total Revenue:</strong> ₹${customers.reduce((sum, c) => sum + c.totalAmount, 0).toFixed(2)}</p>
            <p><strong>Total Paid:</strong> <span style="color: #3b82f6;">₹${customers.reduce((sum, c) => sum + (c.paidAmount || 0), 0).toFixed(2)}</span></p>
            <p><strong>Total Pending:</strong> <span style="color: #ef4444;">₹${customers.reduce((sum, c) => sum + (c.pendingAmount || c.totalAmount), 0).toFixed(2)}</span></p>
          </div>
          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const generateBill = (e) => {
    e.preventDefault();

    // Re-check validation errors right before generating bill using current state values
    const currentErrors = {};
    const phoneCleaned = billData.phone.replace(/\D/g, '');
    const aadharCleaned = billData.aadhar.replace(/\D/g, '');

    if (!phoneCleaned || phoneCleaned.length !== 10) {
      currentErrors.phone = 'Phone number must be 10 digits.';
    }
    if (!aadharCleaned || aadharCleaned.length !== 12) {
      currentErrors.aadhar = 'Aadhar number must be 12 digits.';
    }

    setErrors(currentErrors);

    if (Object.keys(currentErrors).length > 0) {
      alert('Please fix the errors before generating the bill.');
      return; // Prevent bill generation
    }

    // Calculate net weight per packet and total net weight
    const totalRawWeight = billData.packetWeights.reduce((sum, weight) => sum + (parseFloat(weight) || 0), 0);
    const wastePerQuintal = 2; // kg waste per 100 kg
    const totalWaste = (totalRawWeight / 100) * wastePerQuintal;

    // Add extra waste (suta)
    const extraWasteAmount = parseFloat(billData.extraWaste) || 0;
    const totalNetWeight = totalRawWeight - totalWaste - extraWasteAmount;

    // Calculate total amount based on total net weight and price per quintal
    const pricePerKg = (parseFloat(billData.price) || 0) / 100; // Convert price per quintal to price per kg
    let totalAmount = totalNetWeight * pricePerKg;

    // Add bonus to total amount
    const bonusAmount = parseFloat(billData.bonus) || 0;
    totalAmount += bonusAmount;

    // Calculate hamali expensive (per packet deduction)
    const numberOfPackets = parseInt(billData.packets.replace(/\D/g, '')) || 0; // Use cleaned packets value for calculation
    const hamaliExpensiveAmount = numberOfPackets * expensivePriceDeductionPerPacket;

    // Get extra expensive amount
    const extraExpensiveAmount = parseFloat(billData.extraExpensive) || 0;

    // Calculate final amount (Total Amount - Hamali Expensive - Extra Expensive)
    const finalAmount = totalAmount - hamaliExpensiveAmount - extraExpensiveAmount;

    const bill = {
      ...billData,
      // Store cleaned phone and aadhar in bill details for consistent data
      phone: phoneCleaned,
      aadhar: aadharCleaned,
      packets: numberOfPackets.toString(), // Store cleaned and parsed packets number
      totalWeight: totalRawWeight.toFixed(2),
      waste: totalWaste.toFixed(2), // Total waste
      extraWaste: extraWasteAmount.toFixed(2), // Extra waste (suta)
      totalNetWeight: totalNetWeight.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      hamaliExpensive: hamaliExpensiveAmount.toFixed(2), // Store hamali expensive
      extraExpensive: extraExpensiveAmount.toFixed(2), // Store extra expensive
      finalAmount: finalAmount.toFixed(2), // Store final amount
      date: currentTime, // Use currentTime for date and time
      billNumber: `BILL-${Date.now().toString().slice(-6)}`
    };

    // Just set bill details for preview, don't save to customers yet
    setBillDetails(bill);
    setShowBillOutput(true);
    setBillGenerated(true); // Set billGenerated to true after generating bill
  };

  // Create styles for the PDF document
  const styles = StyleSheet.create({
    page: {
      padding: 30,
    },
    header: {
      fontSize: 18,
      marginBottom: 20,
      textAlign: 'center',
      textTransform: 'uppercase',
    },
    section: {
      marginBottom: 15,
      padding: 10,
      borderBottom: '1px solid #eee',
    },
    sectionTitle: {
      fontSize: 14,
      marginBottom: 10,
      fontWeight: 'bold',
    },
    text: {
      fontSize: 12,
      marginBottom: 5,
    },
    table: {
      display: 'table',
      width: 'auto',
      marginBottom: 10,
    },
    tableRow: {
      flexDirection: 'row',
    },
    tableColHeader: {
      width: '50%',
      borderStyle: 'solid',
      borderBottomWidth: 1,
      borderColor: '#eee',
      padding: 5,
      fontWeight: 'bold',
      fontSize: 12,
    },
    tableCol: {
      width: '50%',
      borderStyle: 'solid',
      borderBottomWidth: 1,
      borderColor: '#eee',
      padding: 5,
      fontSize: 10,
    },
    redText: {
      color: 'red',
    },
    greenText: {
      color: 'green',
    },
    footer: {
      fontSize: 10,
      textAlign: 'center',
      marginTop: 20,
    },
  });

  // PDF Bill Component
  const BillDocument = ({ billDetails }) => (
    <Document>
      <Page size="A4" style={styles.page}>
        <View>
          <Text style={styles.header}>Muktankar Traders Kotyal</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Bill Information</Text>
          <Text style={styles.text}>Bill Number: {billDetails.billNumber}</Text>
          <Text style={styles.text}>Date & Time: {billDetails.date}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Farmer Information</Text>
          <Text style={styles.text}>Name: {billDetails.customerName}</Text>
          <Text style={styles.text}>Phone: {billDetails.phone}</Text>
          <Text style={styles.text}>Aadhar: {billDetails.aadhar}</Text>
          <Text style={styles.text}>Village: {billDetails.village}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Item Details</Text>
          <Text style={styles.text}>Type: {billDetails.itemType.toUpperCase()}</Text>
          <Text style={styles.text}>Price per Quintal: ₹{billDetails.price}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Weight Details</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <Text style={styles.tableColHeader}>Packet</Text>
              <Text style={styles.tableColHeader}>Weight (kg)</Text>
            </View>
            {billDetails.packetWeights.map((weight, index) => (
              <View key={index} style={styles.tableRow}>
                <Text style={styles.tableCol}>{index + 1}</Text>
                <Text style={styles.tableCol}>{weight} kg</Text>
              </View>
            ))}
          </View>
          <Text style={styles.text}>Total Weight: {billDetails.totalWeight} kg</Text>
          <Text style={styles.text}>Waste: {billDetails.waste} kg</Text>
          <Text style={styles.text}>Extra Waste (Suta): {billDetails.extraWaste} kg</Text>
          <Text style={styles.text}>Net Weight: {billDetails.totalNetWeight} kg</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Amount Details</Text>
          <Text style={styles.text}>Amount from Net Weight: {(parseFloat(billDetails.totalNetWeight) / 100).toFixed(2)} quintal * ₹{parseFloat(billDetails.price || '0').toFixed(2)} = ₹{(parseFloat(billDetails.totalNetWeight) * (parseFloat(billDetails.price) || 0) / 100).toFixed(2)}</Text>
          {parseFloat(billDetails.bonus) > 0 && (
            <Text style={[styles.text, styles.greenText]}>Bonus: ₹{(parseFloat(billDetails.totalNetWeight) * (parseFloat(billDetails.price) || 0) / 100).toFixed(2)} + ₹{billDetails.bonus} = ₹{(parseFloat(billDetails.totalNetWeight) * (parseFloat(billDetails.price) || 0) / 100 + parseFloat(billDetails.bonus || '0')).toFixed(2)}</Text>
          )}
          {parseFloat(billDetails.hamaliExpensive) > 0 && (
            <Text style={[styles.text, styles.redText]}>Hamali Expensive: ₹{parseFloat(billDetails.totalAmount).toFixed(2)} - ₹{billDetails.hamaliExpensive} = ₹{(parseFloat(billDetails.totalAmount) - parseFloat(billDetails.hamaliExpensive || '0')).toFixed(2)}</Text>
          )}
          {parseFloat(billDetails.extraExpensive) > 0 && (
            <Text style={[styles.text, styles.redText]}>Extra Expensive: ₹{(parseFloat(billDetails.totalAmount) - parseFloat(billDetails.hamaliExpensive || '0')).toFixed(2)} - ₹{billDetails.extraExpensive} = ₹{(parseFloat(billDetails.totalAmount) - parseFloat(billDetails.hamaliExpensive || '0') - parseFloat(billDetails.extraExpensive || '0')).toFixed(2)}</Text>
          )}
          <Text style={[styles.text, { fontWeight: 'bold', marginTop: 10 }]}>Final Amount: ₹{billDetails.finalAmount}</Text>
        </View>

        <View style={styles.footer}>
          <Text>Thank you for your business!</Text>
          <Text>For any queries, please contact us.</Text>
        </View>
      </Page>
    </Document>
  );

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    navigate('/login');
  };

  const handleTransactionSubmit = (e) => {
    e.preventDefault();
    if (!newTransaction.amount || !newTransaction.reason) return;

    const transaction = {
      ...newTransaction,
      id: Date.now(),
      timestamp: currentTime
    };

    setTransactions(prev => [...prev, transaction]);

    // Reset transaction form
    setNewTransaction({
      type: 'credit',
      amount: '',
      reason: '',
      date: currentTime
    });
  };

  // Function to calculate total balance from transactions
  const calculateBalance = useCallback(() => {
    const balance = transactions.reduce((total, transaction) => {
      const amount = parseFloat(transaction.amount) || 0;
      return transaction.type === 'credit'
        ? total + amount
        : total - amount;
    }, 0);
    setTotalBalance(balance);
    return balance;
  }, [transactions]);

  // Update balance whenever transactions change
  useEffect(() => {
    calculateBalance();
  }, [calculateBalance]);

  // Function to calculate total revenue from all bills
  const calculateTotalRevenue = useCallback(() => {
    const revenue = customers.reduce((total, customer) => {
      return total + customer.totalAmount;
    }, 0);
    setAnalytics(prev => ({
      ...prev,
      revenue: revenue
    }));
  }, [customers]);

  // Update revenue whenever customers change
  useEffect(() => {
    calculateTotalRevenue();
  }, [calculateTotalRevenue]);

  const handleSubmitBill = async () => {
    try {
      setIsSubmitting(true);

      if (!billDetails) {
        alert('No bill to submit. Please generate a bill first.');
        return;
      }

      // Update customer information
      const phoneCleaned = billDetails.phone;
      const customerIndex = customers.findIndex(c => c.phone === phoneCleaned);

      if (customerIndex === -1) {
        // New customer
        setCustomers(prev => [...prev, {
          name: billDetails.customerName,
          phone: phoneCleaned,
          aadhar: billDetails.aadhar,
          village: billDetails.village,
          totalBills: 1,
          totalAmount: parseFloat(billDetails.finalAmount),
          paidAmount: 0,
          pendingAmount: parseFloat(billDetails.finalAmount),
          lastPurchase: billDetails.date,
          bills: [billDetails],
          payments: []
        }]);
      } else {
        // Existing customer
        const updatedCustomers = [...customers];
        const newTotalAmount = updatedCustomers[customerIndex].totalAmount + parseFloat(billDetails.finalAmount);
        const currentPaidAmount = updatedCustomers[customerIndex].paidAmount || 0;
        updatedCustomers[customerIndex] = {
          ...updatedCustomers[customerIndex],
          totalBills: updatedCustomers[customerIndex].totalBills + 1,
          totalAmount: newTotalAmount,
          pendingAmount: newTotalAmount - currentPaidAmount,
          lastPurchase: billDetails.date,
          bills: [...updatedCustomers[customerIndex].bills, billDetails],
          payments: updatedCustomers[customerIndex].payments || []
        };
        setCustomers(updatedCustomers);
      }

      // Update product quantities
      updateProductQuantities(billDetails.itemType, parseFloat(billDetails.totalNetWeight));

      // Save bill to Firestore
      await saveBillToFirestore(billDetails);

      // Show success message
      alert('Bill submitted and saved to Firebase successfully!');

      // Reset form and state
      setBillData({
        customerName: '',
        phone: '',
        aadhar: '',
        village: '',
        price: '',
        itemType: 'toor',
        packets: '',
        packetWeights: [],
        bonus: '',
        extraExpensive: '',
        extraWaste: ''
      });
      setBillDetails(null);
      setShowBillOutput(false);
      setBillGenerated(false);
      setBillStep(1);

    } catch (error) {
      alert('Error submitting bill. Please try again.');
      console.error('Error submitting bill:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`dashboard ${darkMode ? 'dark-mode' : ''}`}>
      {/* Mobile Menu Toggle Button */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
      </button>

      {/* Sidebar Overlay */}
      <div
        className={`sidebar-overlay ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      ></div>

      {/* Sidebar */}
      <div className={`dashboard-sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-logo">
          <div className="logo-circle">
            <span>KT</span>
          </div>
          <div className="logo-text">
            <span className="logo-name">Kotyal Traders</span>
            <span className="logo-subtitle">Agricultural Market Place</span>
          </div>
          <button
            className="sidebar-close-btn"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <nav className="sidebar-nav">
          <div
            className={`nav-item ${activePage === 'dashboard' ? 'active' : ''}`}
            onClick={() => {
              setActivePage('dashboard');
              setIsMobileMenuOpen(false);
            }}
          >
            <i className="fas fa-home"></i>
            <span>Dashboard</span>
          </div>
          <div
            className={`nav-item ${activePage === 'billentry' ? 'active' : ''}`}
            onClick={() => {
              setActivePage('billentry');
              setIsMobileMenuOpen(false);
            }}
          >
            <i className="fas fa-file-invoice"></i>
            <span>Bill Entry</span>
          </div>
          <div
            className={`nav-item ${activePage === 'pricemanagement' ? 'active' : ''}`}
            onClick={() => {
              setActivePage('pricemanagement');
              setIsMobileMenuOpen(false);
            }}
          >
            <i className="fas fa-rupee-sign"></i>
            <span>Price Management</span>
          </div>
          <div
            className={`nav-item ${activePage === 'farmers' ? 'active' : ''}`}
            onClick={() => {
              setActivePage('farmers');
              setIsMobileMenuOpen(false);
            }}
          >
            <i className="fas fa-users"></i>
            <span>Farmers</span>
          </div>
          <div
            className={`nav-item ${activePage === 'products' ? 'active' : ''}`}
            onClick={() => {
              setActivePage('products');
              setIsMobileMenuOpen(false);
            }}
          >
            <i className="fas fa-box"></i>
            <span>Products</span>
          </div>
          <div
            className={`nav-item ${activePage === 'revenue' ? 'active' : ''}`}
            onClick={() => {
              setActivePage('revenue');
              setIsMobileMenuOpen(false);
            }}
          >
            <i className="fas fa-chart-line"></i>
            <span>Revenue</span>
          </div>
          <div
            className={`nav-item ${activePage === 'treasury' ? 'active' : ''}`}
            onClick={() => {
              setActivePage('treasury');
              setIsMobileMenuOpen(false);
            }}
          >
            <i className="fas fa-university"></i>
            <span>Treasury</span>
          </div>
          <div
            className={`nav-item ${activePage === 'contactmessages' ? 'active' : ''}`}
            onClick={() => {
              setActivePage('contactmessages');
              setIsMobileMenuOpen(false);
            }}
          >
            <i className="fas fa-envelope"></i>
            <span>Contact Messages</span>
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="nav-item logout-item" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-main-content">
        {/* Render different pages based on activePage */}
        {activePage === 'dashboard' && (
          <>
            {/* Welcome Header */}
            <div className="welcome-header">
              <div className="welcome-text">
                <h1>Welcome back, Admin! 👋</h1>
                <p>Here's what's happening with your business today.</p>
              </div>
              <div className="current-date">
                <i className="fas fa-calendar-alt"></i>
                <span>{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </div>

            {/* Stats Overview Grid */}
            <div className="stats-overview-grid">
              <div className="stat-overview-card" onClick={() => setActivePage('billentry')}>
                <div className="stat-icon-wrapper bills-bg">
                  <i className="fas fa-file-invoice"></i>
                </div>
                <div className="stat-content">
                  <p className="stat-title">Bill Entry</p>
                  <h2 className="stat-number">{customers.reduce((sum, c) => sum + (c.totalBills || 0), 0)}</h2>
                  <div className="stat-change positive">
                    <i className="fas fa-arrow-up"></i>
                    <span>+{analytics.monthlyGrowth.orders}% from last month</span>
                  </div>
                </div>
              </div>
              <div className="stat-overview-card revenue-card-enhanced" onClick={() => setActivePage('revenue')}>
                <div className="stat-icon-wrapper revenue-bg">
                  <i className="fas fa-rupee-sign"></i>
                </div>
                <div className="stat-content">
                  <p className="stat-title">Total Revenue</p>
                  <h2 className="stat-number">₹{(analytics.revenue / 100000).toFixed(1)}L</h2>
                  <div className="stat-change positive">
                    <i className="fas fa-arrow-up"></i>
                    <span>+{analytics.monthlyGrowth.revenue}% from last month</span>
                  </div>
                </div>
              </div>




              <div className="stat-overview-card" onClick={() => setActivePage('farmers')}>
                <div className="stat-icon-wrapper farmers-bg">
                  <i className="fas fa-users"></i>
                </div>
                <div className="stat-content">
                  <p className="stat-title">Total Farmers</p>
                  <h2 className="stat-number">{customers.length}</h2>
                  <div className="stat-change positive">
                    <i className="fas fa-arrow-up"></i>
                    <span>+{analytics.monthlyGrowth.customers}% from last month</span>
                  </div>
                </div>
              </div>



              <div className="stat-overview-card" onClick={() => setActivePage('products')}>
                <div className="stat-icon-wrapper products-bg">
                  <i className="fas fa-box"></i>
                </div>
                <div className="stat-content">
                  <p className="stat-title">Products</p>
                  <h2 className="stat-number">{analytics.products}</h2>
                  <div className="stat-change neutral">
                    <i className="fas fa-minus"></i>
                    <span>Active products</span>
                  </div>
                </div>
              </div>


            </div>

            {/* Quick Actions Section */}
            <div className="quick-actions-enhanced">
              <h3 className="section-title">
                <i className="fas fa-bolt"></i>
                Quick Actions
              </h3>
              <div className="action-cards-grid">
                <div className="action-card" onClick={() => setActivePage('pricemanagement')}>
                  <div className="action-icon">
                    <i className="fas fa-rupee-sign"></i>
                  </div>
                  <div className="action-content">
                    <h4>Price Management</h4>
                    <p>Update product prices quickly</p>
                  </div>
                  <i className="fas fa-arrow-right action-arrow"></i>
                </div>

                <div className="action-card" onClick={() => setActivePage('treasury')}>
                  <div className="action-icon">
                    <i className="fas fa-university"></i>
                  </div>
                  <div className="action-content">
                    <h4>Treasury</h4>
                    <p>Manage treasury transactions</p>
                  </div>
                  <i className="fas fa-arrow-right action-arrow"></i>
                </div>
              </div>
            </div>

            {/* Recent Activity Section */}
            <div className="recent-activity-section">
              <h3 className="section-title">
                <i className="fas fa-clock"></i>
                Recent Activity
              </h3>
              <div className="activity-card">
                {customers.length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-inbox"></i>
                    <p>No recent activity</p>
                    <span>Start by creating your first bill</span>
                  </div>
                ) : (
                  <div className="activity-list">
                    {customers.slice(0, 5).map((customer, index) => (
                      <div key={index} className="activity-item">
                        <div className="activity-icon">
                          <i className="fas fa-user-circle"></i>
                        </div>
                        <div className="activity-details">
                          <p className="activity-title">{customer.name}</p>
                          <span className="activity-meta">
                            {customer.totalBills} bill{customer.totalBills > 1 ? 's' : ''} • ₹{customer.totalAmount.toFixed(2)}
                          </span>
                        </div>
                        <div className="activity-time">
                          <span>{customer.lastPurchase}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Farmers Page */}
        {activePage === 'farmers' && (
          <div className="page-content">
            <div className="page-header">
              <div>
                <h1><i className="fas fa-users"></i> Farmers Management</h1>
                <p>View and manage all your farmers</p>
              </div>
              {customers.length > 0 && (
                <div className="header-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => setShowAllFarmersData(!showAllFarmersData)}
                  >
                    <i className={`fas ${showAllFarmersData ? 'fa-th' : 'fa-table'}`}></i>
                    {showAllFarmersData ? 'Card View' : 'View All Data'}
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handlePrintFarmersData}
                  >
                    <i className="fas fa-print"></i> Print
                  </button>
                </div>
              )}
            </div>

            {customers.length > 0 && (
              <div className="farmers-filters">
                <div className="search-box">
                  <i className="fas fa-search"></i>
                  <input
                    type="text"
                    placeholder="Search by name, phone, or village..."
                    value={farmerSearchQuery}
                    onChange={(e) => setFarmerSearchQuery(e.target.value)}
                  />
                  {farmerSearchQuery && (
                    <button
                      className="clear-search"
                      onClick={() => setFarmerSearchQuery('')}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="farmers-page-content">
              {customers.length === 0 ? (
                <div className="empty-state-large">
                  <i className="fas fa-users"></i>
                  <h3>No farmers yet</h3>
                  <p>Start by creating your first bill to add farmers</p>
                  <button className="btn-primary" onClick={() => setActivePage('billentry')}>
                    <i className="fas fa-plus"></i> Create Bill
                  </button>
                </div>
              ) : getFilteredFarmers().length === 0 ? (
                <div className="empty-state-large">
                  <i className="fas fa-search"></i>
                  <h3>No farmers found</h3>
                  <p>Try adjusting your search criteria</p>
                  <button className="btn-secondary" onClick={() => setFarmerSearchQuery('')}>
                    <i className="fas fa-redo"></i> Clear Search
                  </button>
                </div>
              ) : showAllFarmersData ? (
                <div className="farmers-table-container">
                  <table className="farmers-table">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Village</th>
                        <th>Total Bills</th>
                        <th>Total Amount</th>
                        <th>Paid Amount</th>
                        <th>Pending Amount</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredFarmers().map((customer, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{customer.name}</td>
                          <td>{customer.phone}</td>
                          <td>{customer.village}</td>
                          <td>{customer.totalBills}</td>
                          <td>₹{customer.totalAmount.toFixed(2)}</td>
                          <td style={{ color: '#3b82f6', fontWeight: 'bold' }}>₹{(customer.paidAmount || 0).toFixed(2)}</td>
                          <td style={{ color: '#ef4444', fontWeight: 'bold' }}>₹{(customer.pendingAmount || customer.totalAmount).toFixed(2)}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                className="btn-icon-small"
                                onClick={() => setSelectedCustomer(customer)}
                                title="View Details"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                              <button
                                className="btn-icon-small edit"
                                onClick={() => handleEditFarmer(customer)}
                                title="Edit"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                className="btn-icon-small delete"
                                onClick={() => handleDeleteFarmer(customer)}
                                title="Delete"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="table-summary">
                    <div className="summary-item">
                      <span className="summary-label">Total Farmers:</span>
                      <span className="summary-value">{customers.length}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Revenue:</span>
                      <span className="summary-value">₹{customers.reduce((sum, c) => sum + c.totalAmount, 0).toFixed(2)}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Paid:</span>
                      <span className="summary-value" style={{ color: '#3b82f6' }}>₹{customers.reduce((sum, c) => sum + (c.paidAmount || 0), 0).toFixed(2)}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Pending:</span>
                      <span className="summary-value" style={{ color: '#ef4444' }}>₹{customers.reduce((sum, c) => sum + (c.pendingAmount || c.totalAmount), 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="farmers-grid">
                  {getFilteredFarmers().map((customer, index) => (
                    <div
                      key={index}
                      className="farmer-card"
                    >
                      <div className="farmer-avatar">
                        <i className="fas fa-user-circle"></i>
                      </div>
                      <div className="farmer-info">
                        <h3>{customer.name}</h3>
                        <p><i className="fas fa-phone"></i> {customer.phone}</p>
                        <p><i className="fas fa-map-marker-alt"></i> {customer.village}</p>
                      </div>
                      <div className="farmer-stats">
                        <div className="stat-item">
                          <span className="stat-label">Total Bills</span>
                          <span className="stat-value">{customer.totalBills}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Total Amount</span>
                          <span className="stat-value">₹{customer.totalAmount.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="farmer-actions-buttons">
                        <button
                          className="btn-view"
                          onClick={() => setSelectedCustomer(customer)}
                        >
                          <i className="fas fa-eye"></i> View Details
                        </button>
                        <button
                          className="btn-edit-farmer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditFarmer(customer);
                          }}
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        <button
                          className="btn-delete-farmer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFarmer(customer);
                          }}
                        >
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Farmer Detail Modal */}
        {selectedCustomer && activePage === 'farmers' && (
          <div className="modal-overlay active">
            <div className="modal-content customer-modal-large">
              <div className="modal-header">
                <div className="header-left">
                  <h2><i className="fas fa-user"></i> Farmer Details</h2>
                </div>
                <button className="close-button" onClick={() => setSelectedCustomer(null)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="customer-detail-view">
                <div className="customer-detail-header">
                  <div className="customer-avatar-large">
                    <i className="fas fa-user"></i>
                  </div>
                  <div className="customer-main-info">
                    <h2>{selectedCustomer.name}</h2>
                    <p><i className="fas fa-phone"></i> {selectedCustomer.phone}</p>
                    <p><i className="fas fa-id-card"></i> {selectedCustomer.aadhar}</p>
                    <p><i className="fas fa-map-marker-alt"></i> {selectedCustomer.village}</p>
                  </div>
                  <div className="customer-summary-stats">
                    <div className="summary-stat">
                      <span className="stat-label">Total Bills</span>
                      <span className="stat-value">{selectedCustomer.totalBills}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Total Amount</span>
                      <span className="stat-value">₹{selectedCustomer.totalAmount.toFixed(2)}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Paid Amount</span>
                      <span className="stat-value" style={{ color: '#3b82f6' }}>₹{(selectedCustomer.paidAmount || 0).toFixed(2)}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Pending Amount</span>
                      <span className="stat-value" style={{ color: '#ef4444' }}>₹{(selectedCustomer.pendingAmount || selectedCustomer.totalAmount).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <div className="customer-bills-section">
                  <h3><i className="fas fa-file-invoice-dollar"></i> All Bills with Packet-wise Data</h3>
                  <div className="bills-detailed-list">
                    {selectedCustomer.bills && selectedCustomer.bills.map((bill, billIndex) => (
                      <div key={billIndex} className="bill-detail-card">
                        <div className="bill-card-header">
                          <div className="bill-header-left">
                            <div className="bill-number-badge">{bill.billNumber}</div>
                            <div className="bill-date">{bill.date}</div>
                          </div>
                          <button
                            className="print-bill-btn"
                            onClick={async () => {
                              const blob = await pdf(<BillDocument billDetails={bill} />).toBlob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              const sanitizedCustomerName = bill.customerName.replace(/[^a-zA-Z0-9_]/g, '_');
                              a.download = `${sanitizedCustomerName}_Bill_${bill.billNumber}.pdf`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              window.URL.revokeObjectURL(url);
                            }}
                          >
                            <i className="fas fa-print"></i> Print
                          </button>
                        </div>
                        <div className="bill-card-body">
                          {/* Item Details */}
                          <div className="bill-section-title">
                            <i className="fas fa-box"></i> Item Details
                          </div>
                          <div className="bill-info-row">
                            <span className="info-label">Item Type:</span>
                            <span className="info-value">{getProductDisplayName(bill.itemType)}</span>
                          </div>
                          <div className="bill-info-row">
                            <span className="info-label">Price per Quintal:</span>
                            <span className="info-value">₹{bill.price}</span>
                          </div>

                          {/* Packet Details - HIGHLIGHTED SECTION */}
                          <div className="bill-section-title" style={{ backgroundColor: '#10b981', color: 'white', padding: '10px', borderRadius: '5px', marginTop: '15px' }}>
                            <i className="fas fa-boxes"></i> Packet-wise Weight Data
                          </div>
                          <div className="bill-info-row">
                            <span className="info-label">Total Packets:</span>
                            <span className="info-value" style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{bill.packets}</span>
                          </div>
                          {bill.packetWeights && bill.packetWeights.length > 0 && (
                            <div className="packet-weights-detail">
                              <table className="packet-table" style={{ width: '100%', marginTop: '10px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #10b981' }}>Packet #</th>
                                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #10b981' }}>Weight (kg)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {bill.packetWeights.map((weight, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                      <td style={{ padding: '10px', fontWeight: '500' }}>Packet {idx + 1}</td>
                                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: '#10b981' }}>{weight} kg</td>
                                    </tr>
                                  ))}
                                  <tr style={{ backgroundColor: '#f9fafb', fontWeight: 'bold' }}>
                                    <td style={{ padding: '12px' }}>Total Raw Weight</td>
                                    <td style={{ padding: '12px', textAlign: 'right', color: '#059669' }}>{bill.totalWeight} kg</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Weight Calculations */}
                          <div className="bill-section-title">
                            <i className="fas fa-weight"></i> Weight Calculations
                          </div>
                          <div className="bill-info-row">
                            <span className="info-label">Total Raw Weight:</span>
                            <span className="info-value">{bill.totalWeight} kg</span>
                          </div>
                          <div className="bill-info-row">
                            <span className="info-label">Waste (2kg per quintal):</span>
                            <span className="info-value text-red">{bill.waste} kg</span>
                          </div>
                          {parseFloat(bill.extraWaste) > 0 && (
                            <div className="bill-info-row">
                              <span className="info-label">Extra Waste (Suta):</span>
                              <span className="info-value text-red">{bill.extraWaste} kg</span>
                            </div>
                          )}
                          <div className="bill-info-row">
                            <span className="info-label">Net Weight:</span>
                            <span className="info-value text-green">{bill.totalNetWeight} kg</span>
                          </div>

                          {/* Amount Calculations */}
                          <div className="bill-section-title">
                            <i className="fas fa-calculator"></i> Amount Calculations
                          </div>
                          <div className="bill-info-row">
                            <span className="info-label">Base Amount:</span>
                            <span className="info-value">
                              {(parseFloat(bill.totalNetWeight) / 100).toFixed(2)} quintal × ₹{bill.price} = ₹{(parseFloat(bill.totalNetWeight) * parseFloat(bill.price) / 100).toFixed(2)}
                            </span>
                          </div>
                          {parseFloat(bill.bonus) > 0 && (
                            <div className="bill-info-row">
                              <span className="info-label">Bonus:</span>
                              <span className="info-value text-green">+ ₹{bill.bonus}</span>
                            </div>
                          )}
                          {parseFloat(bill.hamaliExpensive) > 0 && (
                            <div className="bill-info-row">
                              <span className="info-label">Hamali Expensive:</span>
                              <span className="info-value text-red">- ₹{bill.hamaliExpensive}</span>
                            </div>
                          )}
                          {parseFloat(bill.extraExpensive) > 0 && (
                            <div className="bill-info-row">
                              <span className="info-label">Extra Expensive:</span>
                              <span className="info-value text-red">- ₹{bill.extraExpensive}</span>
                            </div>
                          )}
                          <div className="bill-info-row highlight">
                            <span className="info-label">Final Amount:</span>
                            <span className="info-value amount">₹{bill.finalAmount}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Payment Section */}
                  {selectedCustomer.payments && selectedCustomer.payments.length > 0 && (
                    <div className="payments-section" style={{ marginTop: '30px' }}>
                      <h3><i className="fas fa-money-bill-wave"></i> Payment History</h3>
                      <div className="payments-list">
                        {selectedCustomer.payments.map((payment, idx) => (
                          <div key={idx} className="payment-item" style={{ padding: '15px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
                            <div>
                              <p style={{ fontWeight: 'bold', color: '#3b82f6' }}>₹{payment.amount.toFixed(2)}</p>
                              <p style={{ fontSize: '0.9em', color: '#6b7280' }}>{payment.reason}</p>
                            </div>
                            <div style={{ textAlign: 'right', fontSize: '0.85em', color: '#9ca3af' }}>
                              {payment.date}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="modal-actions" style={{ marginTop: '30px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      className="btn-primary"
                      onClick={() => {
                        setShowPaymentModal(true);
                      }}
                    >
                      <i className="fas fa-money-bill"></i> Add Payment
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => handleEditFarmer(selectedCustomer)}
                    >
                      <i className="fas fa-edit"></i> Edit Farmer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Products Page */}
        {activePage === 'products' && (
          <div className="page-content">
            <div className="page-header">
              <div>
                <h1><i className="fas fa-box"></i> Products Management</h1>
                <p>Manage your products and prices</p>
              </div>
              <button
                className="btn-primary"
                onClick={() => {
                  setProductTab('add');
                  setShowProductModal(true);
                }}
              >
                <i className="fas fa-plus"></i> Add New Product
              </button>
            </div>
            <div className="products-page-content">
              <div className="products-grid">
                {products.map((product) => (
                  <div key={product.id} className="product-card-page">
                    <div className="product-header-page">
                      <h3>{product.name}</h3>
                      <span className="product-english">{product.englishName}</span>
                    </div>
                    <div className="product-price-section">
                      <div className="price-display">
                        <span className="price-label">Current Price</span>
                        <span className="price-value">₹{product.currentPrice.toFixed(2)}/quintal</span>
                      </div>
                    </div>
                    <div className="product-actions">
                      <button
                        className="btn-edit"
                        onClick={() => {
                          handleEditProduct(product);
                          setShowProductModal(true);
                        }}
                      >
                        <i className="fas fa-edit"></i> Edit
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteProduct(product.id)}
                      >
                        <i className="fas fa-trash"></i> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Revenue Page */}
        {activePage === 'revenue' && (
          <div className="page-content">
            <div className="page-header">
              <h1><i className="fas fa-rupee-sign"></i> Revenue Management</h1>
              <p>Track your revenue and transactions</p>
            </div>
            <div className="revenue-page-content">
              <div className="revenue-summary-card">
                <div className="revenue-summary-header">
                  <h2>Total Revenue</h2>
                  <div className="revenue-amount">₹{(analytics.revenue / 100000).toFixed(1)}L</div>
                </div>
                <div className="revenue-growth">
                  <i className="fas fa-arrow-up"></i>
                  <span>+{analytics.monthlyGrowth.revenue}% from last month</span>
                </div>
              </div>
              <div className="transactions-section">
                <h3>Recent Transactions</h3>
                {customers.length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-receipt"></i>
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  <div className="transactions-list">
                    {customers.map((customer, index) => (
                      <div key={index} className="transaction-item">
                        <div className="transaction-icon">
                          <i className="fas fa-file-invoice-dollar"></i>
                        </div>
                        <div className="transaction-details">
                          <p className="transaction-name">{customer.name}</p>
                          <span className="transaction-meta">{customer.totalBills} bills</span>
                        </div>
                        <div className="transaction-amount">
                          ₹{customer.totalAmount.toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Price Management Page */}
        {activePage === 'pricemanagement' && (
          <div className="page-content">
            <PriceManagement
              products={products}
              setProducts={setProducts}
              currentTime={currentTime}
            />
          </div>
        )}

        {/* Treasury Page */}
        {activePage === 'treasury' && (
          <div className="page-content">
            <Treasury />
          </div>
        )}

        {/* Contact Messages Page */}
        {activePage === 'contactmessages' && (
          <div className="page-content">
            <ContactMessages />
          </div>
        )}

        {/* Bill Entry Page - Form opens directly via useEffect */}
        {activePage === 'billentry' && showBillForm && (
          <div className="page-content">
            {/* Bill form is rendered in the billing-section below */}
          </div>
        )}
      </div>

      {/* Revenue Modal */}
      {showRevenueModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Revenue Management</h2>
              <div className="current-time">
                Current Time: {currentTime}
              </div>
              <button className="close-button" onClick={() => setShowRevenueModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="tab-buttons">
              <button
                className={activeTab === 'all' ? 'active' : ''}
                onClick={() => setActiveTab('all')}
              >
                All Amounts
              </button>
              <button
                className={activeTab === 'treasury' ? 'active' : ''}
                onClick={() => setActiveTab('treasury')}
              >
                Treasury
              </button>
            </div>

            {activeTab === 'all' ? (
              <div className="all-transactions-card">
                <h3>All Transactions</h3>
                <div className="transactions-list">
                  {transactions.length === 0 ? (
                    <p className="no-transactions">No transactions yet</p>
                  ) : (
                    transactions.map(transaction => (
                      <div key={transaction.id} className={`transaction-item ${transaction.type}`}>
                        <div className="transaction-info">
                          <span className="amount">₹{transaction.amount}</span>
                          <span className="reason">{transaction.reason}</span>
                          <span className="date">{transaction.timestamp}</span>
                        </div>
                        <span className="type">{transaction.type.toUpperCase()}</span>
                      </div>
                    ))
                  )}
                </div>
                <div className="balance">
                  <h3>Current Balance</h3>
                  <p className={`amount ${totalBalance >= 0 ? 'positive' : 'negative'}`}>
                    ₹{Math.abs(totalBalance).toFixed(2)}
                    <span className="balance-type">
                      {totalBalance >= 0 ? 'Credit' : 'Debit'}
                    </span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="treasury-card">
                <h3>Treasury Management</h3>
                <form onSubmit={handleTransactionSubmit} className="transaction-form">
                  <div className="form-group">
                    <label>Transaction Type</label>
                    <select
                      value={newTransaction.type}
                      onChange={(e) => setNewTransaction({ ...newTransaction, type: e.target.value })}
                    >
                      <option value="credit">Credit</option>
                      <option value="debit">Debit</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Amount (₹)</label>
                    <input
                      type="number"
                      value={newTransaction.amount}
                      onChange={(e) => setNewTransaction({ ...newTransaction, amount: e.target.value })}
                      placeholder="Enter amount"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Reason</label>
                    <input
                      type="text"
                      value={newTransaction.reason}
                      onChange={(e) => setNewTransaction({ ...newTransaction, reason: e.target.value })}
                      placeholder="Enter reason for transaction"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Current Time (IST)</label>
                    <div className="current-time-display">{currentTime}</div>
                  </div>
                  <button type="submit" className="submit-btn">Add Transaction</button>
                </form>
              </div>
            )}
          </div>
        </div>
      )
      }



      {/* Product Modal - Add/Edit Only */}
      {showProductModal && (
        <div className="modal-overlay active">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{productTab === 'add' ? 'Add New Product' : 'Edit Product'}</h2>
              <button className="close-button" onClick={() => {
                setShowProductModal(false);
                setProductTab('view');
                setEditingProduct(null);
              }}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            {productTab === 'add' ? (
              <div className="product-form-container">
                <form onSubmit={handleAddProduct} className="product-form">
                  <div className="form-group">
                    <label>Product Name (Kannada)</label>
                    <input
                      type="text"
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                      placeholder="e.g., ತೋಗರೆ"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Product Name (English)</label>
                    <input
                      type="text"
                      value={newProduct.englishName}
                      onChange={(e) => setNewProduct({ ...newProduct, englishName: e.target.value })}
                      placeholder="e.g., Toor"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Initial Price (₹ per quintal)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newProduct.currentPrice}
                      onChange={(e) => setNewProduct({ ...newProduct, currentPrice: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="submit-btn">
                      <i className="fas fa-plus"></i> Add Product
                    </button>
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={() => {
                        setProductTab('view');
                        setNewProduct({ name: '', englishName: '', currentPrice: 0 });
                        setShowProductModal(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            ) : productTab === 'edit' && editingProduct ? (
              <div className="product-form-container">
                <form onSubmit={handleUpdateProduct} className="product-form">
                  <div className="form-group">
                    <label>Product Name (Kannada)</label>
                    <input
                      type="text"
                      value={editingProduct.name}
                      onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Product Name (English)</label>
                    <input
                      type="text"
                      value={editingProduct.englishName}
                      onChange={(e) => setEditingProduct({ ...editingProduct, englishName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Current Price (₹ per quintal)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingProduct.currentPrice}
                      onChange={(e) => setEditingProduct({ ...editingProduct, currentPrice: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="submit-btn">
                      <i className="fas fa-save"></i> Update Product
                    </button>
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={() => {
                        setProductTab('view');
                        setEditingProduct(null);
                        setShowProductModal(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Farmer Modal */}
      {
        showCustomerModal && (
          <div className="modal-overlay">
            <div className="modal-content customer-modal-large">
              <div className="modal-header">
                <div className="header-left">
                  {selectedCustomer && (
                    <button className="back-to-list-btn" onClick={() => setSelectedCustomer(null)}>
                      <i className="fas fa-arrow-left"></i> Back to List
                    </button>
                  )}
                  <h2>{selectedCustomer ? 'Farmer Details' : 'All Farmers'}</h2>
                </div>
                <button className="close-button" onClick={() => {
                  setShowCustomerModal(false);
                  setSelectedCustomer(null);
                }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              {!selectedCustomer ? (
                /* Customer List View */
                <div className="customer-list-view">
                  {customers.length === 0 ? (
                    <p className="no-customers">No farmers yet</p>
                  ) : (
                    <div className="customers-grid">
                      {customers.map((customer, index) => (
                        <div
                          key={index}
                          className="customer-list-card"
                          onClick={() => setSelectedCustomer(customer)}
                        >
                          <div className="customer-avatar">
                            <i className="fas fa-user"></i>
                          </div>
                          <div className="customer-info">
                            <h3>{customer.name}</h3>
                            <p className="customer-phone">
                              <i className="fas fa-phone"></i> {customer.phone}
                            </p>
                            {customer.bills && customer.bills.length > 0 && (
                              <p className="customer-last-item">
                                {getProductDisplayName(customer.bills[customer.bills.length - 1].itemType)} - {customer.bills[customer.bills.length - 1].packets} Packets
                              </p>
                            )}
                            <div className="customer-stats">
                              <span className="stat-badge">
                                <i className="fas fa-file-invoice"></i> {customer.totalBills} Bills
                              </span>
                              <span className="stat-badge amount">
                                <i className="fas fa-rupee-sign"></i> {customer.totalAmount.toFixed(2)}
                              </span>
                            </div>
                          </div>
                          <div className="view-details-icon">
                            <i className="fas fa-chevron-right"></i>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* Customer Detail View */
                <div className="customer-detail-view">
                  <div className="customer-detail-header">
                    <div className="customer-avatar-large">
                      <i className="fas fa-user"></i>
                    </div>
                    <div className="customer-main-info">
                      <h2>{selectedCustomer.name}</h2>
                      <p><i className="fas fa-phone"></i> {selectedCustomer.phone}</p>
                      <p><i className="fas fa-id-card"></i> {selectedCustomer.aadhar}</p>
                    </div>
                    <div className="customer-summary-stats">
                      <div className="summary-stat">
                        <span className="stat-label">Total Bills</span>
                        <span className="stat-value">{selectedCustomer.totalBills}</span>
                      </div>
                      <div className="summary-stat">
                        <span className="stat-label">Total Amount</span>
                        <span className="stat-value">₹{selectedCustomer.totalAmount.toFixed(2)}</span>
                      </div>
                      <div className="summary-stat">
                        <span className="stat-label">Last Purchase</span>
                        <span className="stat-value-small">{selectedCustomer.lastPurchase}</span>
                      </div>
                    </div>
                  </div>

                  <div className="customer-bills-section">
                    <h3><i className="fas fa-file-invoice-dollar"></i> All Bills</h3>
                    <div className="bills-detailed-list">
                      {selectedCustomer.bills.map((bill, billIndex) => (
                        <div key={billIndex} className="bill-detail-card">
                          <div className="bill-card-header">
                            <div className="bill-header-left">
                              <div className="bill-number-badge">{bill.billNumber}</div>
                              <div className="bill-date">{bill.date}</div>
                            </div>
                            <button
                              className="print-bill-btn"
                              onClick={async () => {
                                const blob = await pdf(<BillDocument billDetails={bill} />).toBlob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                const sanitizedCustomerName = bill.customerName.replace(/[^a-zA-Z0-9_]/g, '_');
                                a.download = `${sanitizedCustomerName}_Bill_${bill.billNumber}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                window.URL.revokeObjectURL(url);
                              }}
                            >
                              <i className="fas fa-print"></i> Print
                            </button>
                          </div>
                          <div className="bill-card-body">
                            {/* Item Details */}
                            <div className="bill-section-title">
                              <i className="fas fa-box"></i> Item Details
                            </div>
                            <div className="bill-info-row">
                              <span className="info-label">Item Type:</span>
                              <span className="info-value">{getProductDisplayName(bill.itemType)}</span>
                            </div>
                            <div className="bill-info-row">
                              <span className="info-label">Price per Quintal:</span>
                              <span className="info-value">₹{bill.price}</span>
                            </div>

                            {/* Packet Details */}
                            <div className="bill-section-title">
                              <i className="fas fa-boxes"></i> Packet Details
                            </div>
                            <div className="bill-info-row">
                              <span className="info-label">Total Packets:</span>
                              <span className="info-value">{bill.packets}</span>
                            </div>
                            {bill.packetWeights && bill.packetWeights.length > 0 && (
                              <div className="packet-weights-detail">
                                <table className="packet-table">
                                  <thead>
                                    <tr>
                                      <th>Packet #</th>
                                      <th>Weight (kg)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {bill.packetWeights.map((weight, idx) => (
                                      <tr key={idx}>
                                        <td>Packet {idx + 1}</td>
                                        <td>{weight} kg</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Weight Calculations */}
                            <div className="bill-section-title">
                              <i className="fas fa-weight"></i> Weight Calculations
                            </div>
                            <div className="bill-info-row">
                              <span className="info-label">Total Raw Weight:</span>
                              <span className="info-value">{bill.totalWeight} kg</span>
                            </div>
                            <div className="bill-info-row">
                              <span className="info-label">Waste (2kg per quintal):</span>
                              <span className="info-value text-red">{bill.waste} kg</span>
                            </div>
                            {parseFloat(bill.extraWaste) > 0 && (
                              <div className="bill-info-row">
                                <span className="info-label">Extra Waste (Suta):</span>
                                <span className="info-value text-red">{bill.extraWaste} kg</span>
                              </div>
                            )}
                            <div className="bill-info-row">
                              <span className="info-label">Net Weight:</span>
                              <span className="info-value text-green">{bill.totalNetWeight} kg</span>
                            </div>

                            {/* Amount Calculations */}
                            <div className="bill-section-title">
                              <i className="fas fa-calculator"></i> Amount Calculations
                            </div>
                            <div className="bill-info-row">
                              <span className="info-label">Base Amount:</span>
                              <span className="info-value">
                                {(parseFloat(bill.totalNetWeight) / 100).toFixed(2)} quintal × ₹{bill.price} = ₹{(parseFloat(bill.totalNetWeight) * parseFloat(bill.price) / 100).toFixed(2)}
                              </span>
                            </div>
                            {parseFloat(bill.bonus) > 0 && (
                              <div className="bill-info-row">
                                <span className="info-label">Bonus:</span>
                                <span className="info-value text-green">+ ₹{bill.bonus}</span>
                              </div>
                            )}
                            {parseFloat(bill.hamaliExpensive) > 0 && (
                              <div className="bill-info-row">
                                <span className="info-label">Hamali Expensive:</span>
                                <span className="info-value text-red">- ₹{bill.hamaliExpensive}</span>
                              </div>
                            )}
                            {parseFloat(bill.extraExpensive) > 0 && (
                              <div className="bill-info-row">
                                <span className="info-label">Extra Expensive:</span>
                                <span className="info-value text-red">- ₹{bill.extraExpensive}</span>
                              </div>
                            )}
                            <div className="bill-info-row highlight">
                              <span className="info-label">Final Amount:</span>
                              <span className="info-value amount">₹{bill.finalAmount}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      }

      <div className="billing-section">
        {showBillForm && (
          <>
            <div className="form-card">
              <div className="form-header">
                <h2 className="form-title">
                  <i className="fas fa-file-invoice"></i>
                  Generate Bill - Step {billStep} of 3
                </h2>
              </div>

              {/* Step Progress Indicator */}
              <div className="step-progress">
                <div className={`step ${billStep >= 1 ? 'active' : ''}`}>
                  <div className="step-number">1</div>
                  <div className="step-label">Farmer Details</div>
                </div>
                <div className={`step ${billStep >= 2 ? 'active' : ''}`}>
                  <div className="step-number">2</div>
                  <div className="step-label">Item Details</div>
                </div>
                <div className={`step ${billStep >= 3 ? 'active' : ''}`}>
                  <div className="step-number">3</div>
                  <div className="step-label">Packet Entry</div>
                </div>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                console.log('Form submitted! Current step:', billStep);
                if (billStep === 1) {
                  // Validate Step 1
                  const phoneCleaned = billData.phone.replace(/\D/g, '');
                  const aadharCleaned = billData.aadhar.replace(/\D/g, '');
                  const currentErrors = {};

                  if (!billData.customerName) {
                    currentErrors.customerName = 'Farmer name is required.';
                  }
                  if (!phoneCleaned || phoneCleaned.length !== 10) {
                    currentErrors.phone = 'Phone number must be 10 digits.';
                  }
                  if (!aadharCleaned || aadharCleaned.length !== 12) {
                    currentErrors.aadhar = 'Aadhar number must be 12 digits.';
                  }
                  if (!billData.village) {
                    currentErrors.village = 'Village is required.';
                  }

                  setErrors(currentErrors);
                  if (Object.keys(currentErrors).length === 0) {
                    setBillStep(2);
                  }
                } else if (billStep === 2) {
                  // Validate Step 2
                  const currentErrors = {};
                  if (!billData.price || parseFloat(billData.price) <= 0) {
                    currentErrors.price = 'Price per quintal is required.';
                  }
                  if (!billData.itemType) {
                    currentErrors.itemType = 'Item type is required.';
                  }

                  setErrors(currentErrors);
                  if (Object.keys(currentErrors).length === 0) {
                    // Initialize with empty packet array when moving to step 3
                    if (billData.packetWeights.length === 0) {
                      setBillData(prev => ({
                        ...prev,
                        packetWeights: [],
                        packets: '0'
                      }));
                    }
                    setBillStep(3);
                  }
                } else if (billStep === 3) {
                  // Step 3 - Generate bill only if packets exist
                  // Don't show error, just don't generate if no packets
                  if (billData.packetWeights.length > 0) {
                    // Check if all packet weights are filled
                    const hasEmptyWeights = billData.packetWeights.some(weight => !weight || parseFloat(weight) <= 0);
                    if (hasEmptyWeights) {
                      alert('Please enter valid weights for all packets.');
                      return;
                    }
                    // Generate bill
                    generateBill(e);
                  }
                }
              }} className="bill-form">

                {/* Step 1: Farmer Details */}
                {billStep === 1 && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="customerName">
                          <i className="fas fa-user"></i> Farmer Name
                        </label>
                        <input
                          type="text"
                          id="customerName"
                          value={billData.customerName}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter farmer name"
                          autoComplete="off"
                        />
                        {errors.customerName && <span className="error-message">{errors.customerName}</span>}
                      </div>
                      <div className="form-group">
                        <label htmlFor="phone">
                          <i className="fas fa-phone-alt"></i> Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          value={billData.phone}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter 10-digit phone number"
                          autoComplete="off"
                        />
                        {errors.phone && <span className="error-message">{errors.phone}</span>}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="aadhar">
                          <i className="fas fa-id-card"></i> Aadhar Number
                        </label>
                        <input
                          type="text"
                          id="aadhar"
                          value={billData.aadhar}
                          onChange={handleInputChange}
                          maxLength="14"
                          required
                          placeholder="Enter 12-digit Aadhar number"
                          autoComplete="off"
                        />
                        {errors.aadhar && <span className="error-message">{errors.aadhar}</span>}
                      </div>
                      <div className="form-group">
                        <label htmlFor="village">
                          <i className="fas fa-map-marker-alt"></i> Village
                        </label>
                        <input
                          type="text"
                          id="village"
                          value={billData.village}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter village name"
                          autoComplete="off"
                        />
                        {errors.village && <span className="error-message">{errors.village}</span>}
                      </div>
                    </div>
                  </>
                )}

                {/* Step 2: Item Details */}
                {billStep === 2 && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="price">
                          <i className="fas fa-rupee-sign"></i> Price per Quintal
                        </label>
                        <input
                          type="number"
                          id="price"
                          value={billData.price}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter price per quintal"
                        />
                        {errors.price && <span className="error-message">{errors.price}</span>}
                      </div>
                      <div className="form-group">
                        <label htmlFor="itemType">
                          <i className="fas fa-box"></i> Item Type
                        </label>
                        <select
                          id="itemType"
                          value={billData.itemType}
                          onChange={handleInputChange}
                          required
                        >
                          {products.map(product => (
                            <option key={product.id} value={product.key}>
                              {product.name} ({product.englishName})
                            </option>
                          ))}
                        </select>
                        {errors.itemType && <span className="error-message">{errors.itemType}</span>}
                      </div>
                    </div>
                  </>
                )}

                {/* Step 3: Packet Entry */}
                {billStep === 3 && (
                  <>
                    <div className="packet-entry-section">
                      <div className="packet-entry-header">
                        <h4>
                          <i className="fas fa-boxes"></i> Packet Weights
                        </h4>
                        <p className="packet-count">Total Packets: {billData.packetWeights.length}</p>
                      </div>

                      {billData.packetWeights.length > 0 && (
                        <div className="packet-list">
                          {billData.packetWeights.map((weight, index) => (
                            <div key={index} className="packet-item">
                              <div className="packet-number">
                                <i className="fas fa-box"></i> Packet {index + 1}
                              </div>
                              <div className="packet-weight-input">
                                <input
                                  ref={(el) => (packetInputRefs.current[index] = el)}
                                  type="number"
                                  step="0.01"
                                  value={weight}
                                  onChange={(e) => handlePacketWeightChange(index, e.target.value)}
                                  placeholder="Enter weight (kg)"
                                  required
                                />
                                <span className="unit-label">kg</span>
                              </div>
                              <button
                                type="button"
                                className="remove-packet-btn"
                                onClick={() => {
                                  const newWeights = billData.packetWeights.filter((_, i) => i !== index);
                                  setBillData(prev => ({
                                    ...prev,
                                    packetWeights: newWeights,
                                    packets: newWeights.length.toString()
                                  }));
                                }}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <button
                        type="button"
                        className="add-packet-btn"
                        onClick={() => {
                          const newWeights = [...billData.packetWeights, ''];
                          setBillData(prev => ({
                            ...prev,
                            packetWeights: newWeights,
                            packets: newWeights.length.toString()
                          }));
                          // Focus on the newly added input field
                          setTimeout(() => {
                            const newIndex = newWeights.length - 1;
                            if (packetInputRefs.current[newIndex]) {
                              packetInputRefs.current[newIndex].focus();
                            }
                          }, 0);
                        }}
                      >
                        <i className="fas fa-save"></i> Save and Add New Packet
                      </button>

                      {billData.packetWeights.length === 0 && (
                        <p className="no-packets-message">
                          <i className="fas fa-info-circle"></i> Click "Save and Add New Packet" to start entering packet weights
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Navigation Buttons */}
                <div className="form-navigation">
                  {billStep > 1 && (
                    <button type="button" className="prev-btn" onClick={() => setBillStep(billStep - 1)}>
                      <i className="fas fa-arrow-left"></i> Previous
                    </button>
                  )}
                  <button
                    type="submit"
                    className="submit-btn"
                    style={{
                      pointerEvents: 'auto',
                      touchAction: 'manipulation',
                      cursor: 'pointer',
                      zIndex: 2147483647,
                      position: 'relative',
                      isolation: 'isolate'
                    }}
                    onClick={(e) => {
                      console.log('Button clicked directly!', e);
                      // Force form submission
                      if (e.currentTarget.form) {
                        e.currentTarget.form.requestSubmit();
                      }
                    }}
                    onMouseDown={(e) => {
                      console.log('Mouse down on button!');
                      e.stopPropagation();
                    }}
                    onTouchStart={(e) => {
                      console.log('Touch start on button!');
                      e.stopPropagation();
                    }}
                  >
                    {billStep < 3 ? (
                      <>
                        <i className="fas fa-save"></i> Save and Next
                      </>
                    ) : (
                      <>
                        <i className="fas fa-calculator"></i> Generate Bill
                      </>
                    )}
                  </button>
                </div>

                {billGenerated && (
                  <div className="additional-fields">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="bonus">
                          <i className="fas fa-gift"></i> Bonus Amount (₹)
                        </label>
                        <input
                          type="number"
                          id="bonus"
                          value={billData.bonus}
                          onChange={handleInputChange}
                          placeholder="Enter bonus amount"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="extraExpensive">
                          <i className="fas fa-money-bill-wave"></i> Extra Expensive (₹)
                        </label>
                        <input
                          type="number"
                          id="extraExpensive"
                          value={billData.extraExpensive}
                          onChange={handleInputChange}
                          placeholder="Enter extra expensive amount"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="extraWaste">
                          <i className="fas fa-trash"></i> Extra Waste (Suta) (kg)
                        </label>
                        <input
                          type="number"
                          id="extraWaste"
                          value={billData.extraWaste}
                          onChange={handleInputChange}
                          placeholder="Enter extra waste amount"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {showBillOutput && billDetails && (
              <div className="bill-output">
                <h2 className="form-title">
                  <i className="fas fa-file-invoice-dollar"></i>
                  Bill Details
                </h2>
                <div className="bill-details">
                  <div className="bill-header">
                    <h3>Muktankar Traders Kotyal</h3>
                    <p>Bill Number: {billDetails.billNumber}</p>
                    <p>Date: {billDetails.date}</p>
                  </div>

                  <div className="bill-section">
                    <h4>Customer Information</h4>
                    <p><strong>Name:</strong> {billDetails.customerName}</p>
                    <p><strong>Phone:</strong> {billDetails.phone}</p>
                    <p><strong>Aadhar:</strong> {billDetails.aadhar}</p>
                    <p><strong>Village:</strong> {billDetails.village}</p>
                  </div>

                  <div className="bill-section">
                    <h4>Item Details</h4>
                    <p><strong>Type:</strong> {getProductDisplayName(billDetails.itemType)}</p>
                    <p><strong>Price per Quintal:</strong> ₹{billDetails.price}</p>
                  </div>

                  <div className="bill-section">
                    <h4>Weight Details</h4>
                    <div className="weights-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Packet</th>
                            <th>Weight (kg)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {billDetails.packetWeights.map((weight, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{weight} kg</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p><strong>Total Weight:</strong> {billDetails.totalWeight} kg</p>
                    <p><strong>Waste:</strong> {billDetails.waste} kg</p>
                    <p><strong>Extra Waste (Suta):</strong> {billDetails.extraWaste} kg</p>
                    <p><strong>Net Weight:</strong> {billDetails.totalNetWeight} kg</p>
                  </div>

                  <div className="bill-section">
                    <h4>Amount Details</h4>
                    <p><strong>Amount from Net Weight:</strong> {(parseFloat(billDetails.totalNetWeight) / 100).toFixed(2)} quintal * ₹{parseFloat(billDetails.price || '0').toFixed(2)} = ₹{(parseFloat(billDetails.totalNetWeight) * (parseFloat(billDetails.price) || 0) / 100).toFixed(2)}</p>
                    {parseFloat(billDetails.bonus) > 0 && (
                      <p style={{ color: 'green' }}><strong>Bonus:</strong> ₹{(parseFloat(billDetails.totalNetWeight) * (parseFloat(billDetails.price) || 0) / 100).toFixed(2)} + ₹{billDetails.bonus} = ₹{(parseFloat(billDetails.totalNetWeight) * (parseFloat(billDetails.price) || 0) / 100 + parseFloat(billDetails.bonus || '0')).toFixed(2)}</p>
                    )}
                    {parseFloat(billDetails.hamaliExpensive) > 0 && (
                      <p style={{ color: 'red' }}><strong>Hamali Expensive:</strong> ₹{parseFloat(billDetails.totalAmount).toFixed(2)} - ₹{billDetails.hamaliExpensive} = ₹{(parseFloat(billDetails.totalAmount) - parseFloat(billDetails.hamaliExpensive || '0')).toFixed(2)}</p>
                    )}
                    {parseFloat(billDetails.extraExpensive) > 0 && (
                      <p style={{ color: 'red' }}><strong>Extra Expensive:</strong> ₹{(parseFloat(billDetails.totalAmount) - parseFloat(billDetails.hamaliExpensive || '0')).toFixed(2)} - ₹{billDetails.extraExpensive} = ₹{(parseFloat(billDetails.totalAmount) - parseFloat(billDetails.hamaliExpensive || '0') - parseFloat(billDetails.extraExpensive || '0')).toFixed(2)}</p>
                    )}
                    <p className="final-amount"><strong>Final Amount:</strong> ₹{billDetails.finalAmount}</p>
                  </div>
                </div>

                <div className="bill-actions">
                  <button
                    onClick={handleSubmitBill}
                    className="submit-btn"
                    disabled={isSubmitting}
                  >
                    <i className="fas fa-check"></i>
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Farmer Details Modal */}
      {selectedCustomer && (
        <div className="modal-overlay" onClick={() => setSelectedCustomer(null)}>
          <div className="modal-content farmer-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-user"></i> {selectedCustomer.name}</h2>
              <button className="close-button" onClick={() => setSelectedCustomer(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="farmer-details-content">
              <div className="farmer-summary-card">
                <div className="summary-info">
                  <p><i className="fas fa-phone"></i> {selectedCustomer.phone}</p>
                  <p><i className="fas fa-id-card"></i> {selectedCustomer.aadhar}</p>
                  <p><i className="fas fa-map-marker-alt"></i> {selectedCustomer.village}</p>
                </div>
                <div className="summary-stats">
                  <div className="summary-stat-item">
                    <span className="stat-label">Total Bills</span>
                    <span className="stat-value">{selectedCustomer.totalBills}</span>
                  </div>
                  <div className="summary-stat-item">
                    <span className="stat-label">Total Amount</span>
                    <span className="stat-value">₹{selectedCustomer.totalAmount.toFixed(2)}</span>
                  </div>
                  <div className="summary-stat-item">
                    <span className="stat-label">Paid Amount</span>
                    <span className="stat-value paid">₹{(selectedCustomer.paidAmount || 0).toFixed(2)}</span>
                  </div>
                  <div className="summary-stat-item">
                    <span className="stat-label">Pending Amount</span>
                    <span className="stat-value pending">₹{(selectedCustomer.pendingAmount || selectedCustomer.totalAmount).toFixed(2)}</span>
                  </div>
                </div>
                <button
                  className="add-payment-btn"
                  onClick={() => setShowPaymentModal(true)}
                >
                  <i className="fas fa-money-bill-wave"></i> Add Payment
                </button>
              </div>

              {selectedCustomer.payments && selectedCustomer.payments.length > 0 && (
                <div className="payments-section">
                  <h3 className="payments-heading">Payment History</h3>
                  <div className="payments-list">
                    {selectedCustomer.payments.map((payment, index) => (
                      <div key={index} className="payment-item">
                        <div className="payment-icon">
                          <i className="fas fa-check-circle"></i>
                        </div>
                        <div className="payment-details">
                          <span className="payment-amount">₹{payment.amount.toFixed(2)}</span>
                          <span className="payment-reason">{payment.reason || payment.note || 'Payment received'}</span>
                          <span className="payment-date">{payment.date}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <h3 className="bills-heading">All Bills</h3>
              <div className="bills-list">
                {selectedCustomer.bills && selectedCustomer.bills.map((bill, index) => (
                  <div key={index} className="bill-detail-card">
                    <div className="bill-card-header">
                      <h4>Bill #{bill.billNumber}</h4>
                      <button
                        className="print-bill-btn"
                        onClick={() => {
                          const printWindow = window.open('', '_blank');
                          const productName = products.find(p => p.key === bill.itemType)?.name || bill.itemType;
                          const productEnglishName = products.find(p => p.key === bill.itemType)?.englishName || bill.itemType;

                          printWindow.document.write(`
                            <!DOCTYPE html>
                            <html>
                              <head>
                                <title>Bill Details - ${bill.billNumber}</title>
                                <style>
                                  body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
                                  h1 { color: #10b981; text-align: center; margin-bottom: 5px; }
                                  .header-info { text-align: center; margin-bottom: 20px; color: #666; }
                                  .section { margin: 20px 0; padding: 15px; border: 1px solid #ddd; border-radius: 8px; }
                                  .section h3 { color: #10b981; margin-top: 0; border-bottom: 2px solid #10b981; padding-bottom: 8px; }
                                  .info-row { display: flex; justify-content: space-between; margin: 8px 0; }
                                  .label { font-weight: bold; color: #666; }
                                  .value { color: #333; }
                                  table { width: 100%; border-collapse: collapse; margin: 10px 0; }
                                  th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
                                  th { background-color: #10b981; color: white; }
                                  .final-amount { font-size: 20px; font-weight: bold; color: #10b981; text-align: right; margin-top: 20px; }
                                  @media print { button { display: none; } }
                                </style>
                              </head>
                              <body>
                                <h1>Muktankar Traders Kotyal</h1>
                                <div class="header-info">
                                  <p><strong>Bill Number:</strong> ${bill.billNumber}</p>
                                  <p><strong>Date:</strong> ${bill.date}</p>
                                </div>

                                <div class="section">
                                  <h3>Customer Information</h3>
                                  <div class="info-row"><span class="label">Name:</span><span class="value">${bill.customerName}</span></div>
                                  <div class="info-row"><span class="label">Phone:</span><span class="value">${bill.phone}</span></div>
                                  <div class="info-row"><span class="label">Aadhar:</span><span class="value">${bill.aadhar}</span></div>
                                  <div class="info-row"><span class="label">Village:</span><span class="value">${bill.village}</span></div>
                                </div>

                                <div class="section">
                                  <h3>Item Details</h3>
                                  <div class="info-row"><span class="label">Type:</span><span class="value">${productName} (${productEnglishName})</span></div>
                                  <div class="info-row"><span class="label">Price per Quintal:</span><span class="value">₹${parseFloat(bill.price).toFixed(2)}</span></div>
                                </div>

                                <div class="section">
                                  <h3>Weight Details</h3>
                                  <table>
                                    <thead><tr><th>Packet</th><th>Weight (kg)</th></tr></thead>
                                    <tbody>
                                      ${bill.packetWeights.map((weight, i) => `<tr><td>${i + 1}</td><td>${weight} kg</td></tr>`).join('')}
                                    </tbody>
                                  </table>
                                  <div class="info-row"><span class="label">Total Weight:</span><span class="value">${bill.totalWeight} kg</span></div>
                                  <div class="info-row"><span class="label">Waste:</span><span class="value">${bill.waste} kg</span></div>
                                  <div class="info-row"><span class="label">Extra Waste (Suta):</span><span class="value">${bill.extraWaste} kg</span></div>
                                  <div class="info-row"><span class="label">Net Weight:</span><span class="value">${bill.totalNetWeight} kg</span></div>
                                </div>

                                <div class="section">
                                  <h3>Amount Details</h3>
                                  <div class="info-row"><span class="label">Amount from Net Weight:</span><span class="value">${bill.quintals} quintal × ₹${parseFloat(bill.price).toFixed(2)} = ₹${bill.totalAmount}</span></div>
                                  ${parseFloat(bill.bonus) > 0 ? `<div class="info-row"><span class="label">Bonus:</span><span class="value">₹${bill.totalAmount} + ₹${bill.bonus} = ₹${(parseFloat(bill.totalAmount) + parseFloat(bill.bonus)).toFixed(2)}</span></div>` : ''}
                                  ${parseFloat(bill.hamaliExpensive) > 0 ? `<div class="info-row"><span class="label">Hamali Expensive:</span><span class="value">₹${(parseFloat(bill.totalAmount) + parseFloat(bill.bonus || 0)).toFixed(2)} - ₹${bill.hamaliExpensive} = ₹${(parseFloat(bill.totalAmount) + parseFloat(bill.bonus || 0) - parseFloat(bill.hamaliExpensive)).toFixed(2)}</span></div>` : ''}
                                  ${parseFloat(bill.extraExpensive) > 0 ? `<div class="info-row"><span class="label">Extra Expensive:</span><span class="value">- ₹${bill.extraExpensive}</span></div>` : ''}
                                  <div class="final-amount">Final Amount: ₹${bill.finalAmount}</div>
                                </div>

                                <script>window.onload = function() { window.print(); }</script>
                              </body>
                            </html>
                          `);
                          printWindow.document.close();
                        }}
                      >
                        <i className="fas fa-print"></i> Print
                      </button>
                    </div>
                    <div className="bill-info-grid">
                      <div className="bill-info-item">
                        <span className="info-label">Date</span>
                        <span className="info-value">{bill.date}</span>
                      </div>
                      <div className="bill-info-item">
                        <span className="info-label">Item Type</span>
                        <span className="info-value">{products.find(p => p.key === bill.itemType)?.name || bill.itemType}</span>
                      </div>
                      <div className="bill-info-item">
                        <span className="info-label">Price/Quintal</span>
                        <span className="info-value">₹{parseFloat(bill.price).toFixed(2)}</span>
                      </div>
                      <div className="bill-info-item">
                        <span className="info-label">Packets</span>
                        <span className="info-value">{bill.packetWeights.length}</span>
                      </div>
                      <div className="bill-info-item">
                        <span className="info-label">Total Weight</span>
                        <span className="info-value">{bill.totalWeight} kg</span>
                      </div>
                      <div className="bill-info-item">
                        <span className="info-label">Net Weight</span>
                        <span className="info-value">{bill.totalNetWeight} kg</span>
                      </div>
                      <div className="bill-info-item">
                        <span className="info-label">Final Amount</span>
                        <span className="info-value amount-highlight">₹{bill.finalAmount}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedCustomer && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-money-bill-wave"></i> Add Payment</h2>
              <button className="close-button" onClick={() => setShowPaymentModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="payment-modal-content">
              <div className="payment-info-card">
                <h3>{selectedCustomer.name}</h3>
                <div className="payment-summary">
                  <div className="payment-summary-item">
                    <span className="label">Total Amount:</span>
                    <span className="value">₹{selectedCustomer.totalAmount.toFixed(2)}</span>
                  </div>
                  <div className="payment-summary-item">
                    <span className="label">Paid Amount:</span>
                    <span className="value paid">₹{(selectedCustomer.paidAmount || 0).toFixed(2)}</span>
                  </div>
                  <div className="payment-summary-item highlight">
                    <span className="label">Pending Amount:</span>
                    <span className="value pending">₹{(selectedCustomer.pendingAmount || selectedCustomer.totalAmount).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <div className="payment-form">
                <div className="form-group">
                  <label>Payment Type <span className="required">*</span></label>
                  <select
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                    className="form-select"
                  >
                    <option value="treasury">Treasury (ಖಜಾನೆ) - Deduct from treasury</option>
                    <option value="online">Online (ಆನ್‌ಲೈನ್) - No treasury deduction</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Payment Amount <span className="required">*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="Enter amount"
                    max={selectedCustomer.pendingAmount || selectedCustomer.totalAmount}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Reason / Note <span className="required">*</span></label>
                  <textarea
                    value={paymentReason}
                    onChange={(e) => setPaymentReason(e.target.value)}
                    placeholder="e.g., Cash payment, Bank transfer, Partial payment, etc."
                    rows="3"
                    required
                  />
                </div>
                <div className="form-actions">
                  <button className="submit-btn" onClick={handleAddPayment}>
                    <i className="fas fa-check"></i> Record Payment
                  </button>
                  <button className="cancel-btn" onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentAmount('');
                    setPaymentReason('');
                    setPaymentType('treasury');
                  }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Farmer Modal */}
      {showEditFarmerModal && editingFarmer && (
        <div className="modal-overlay" onClick={() => setShowEditFarmerModal(false)}>
          <div className="modal-content edit-farmer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-user-edit"></i> Edit Farmer Details</h2>
              <button className="close-button" onClick={() => setShowEditFarmerModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="edit-farmer-content">
              <div className="form-group">
                <label>Farmer Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={editingFarmer.name}
                  onChange={(e) => setEditingFarmer({ ...editingFarmer, name: e.target.value })}
                  placeholder="Enter farmer name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone Number <span className="required">*</span></label>
                <input
                  type="tel"
                  value={editingFarmer.phone}
                  onChange={(e) => setEditingFarmer({ ...editingFarmer, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  placeholder="Enter 10-digit phone number"
                  maxLength="10"
                  required
                />
              </div>
              <div className="form-group">
                <label>Aadhar Number</label>
                <input
                  type="text"
                  value={editingFarmer.aadhar}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/\D/g, '').slice(0, 12);
                    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
                    setEditingFarmer({ ...editingFarmer, aadhar: formatted });
                  }}
                  placeholder="Enter 12-digit Aadhar number"
                  maxLength="14"
                />
              </div>
              <div className="form-group">
                <label>Village <span className="required">*</span></label>
                <input
                  type="text"
                  value={editingFarmer.village}
                  onChange={(e) => setEditingFarmer({ ...editingFarmer, village: e.target.value })}
                  placeholder="Enter village name"
                  required
                />
              </div>
              <div className="form-actions">
                <button className="submit-btn" onClick={handleUpdateFarmer}>
                  <i className="fas fa-save"></i> Update Farmer
                </button>
                <button className="cancel-btn" onClick={() => {
                  setShowEditFarmerModal(false);
                  setEditingFarmer(null);
                }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Firebase Status Indicator */}
      <FirebaseStatus />

      {/* Firebase Test Component - Remove after testing */}
      <FirebaseTest />
    </div >
  );
};

export default Business; 